import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions, Tag, Button, Space, Table, message, Typography,
} from 'antd';
import { ArrowLeftOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getDeduplicationRule,
  runDeduplicationJob,
  type DeduplicationRule,
  type DeduplicationMatchField,
} from '@/api/deduplicationApi';
import { useAuth } from '@/hooks/useAuth';

const { Title } = Typography;

export default function DeduplicationRuleDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [rule, setRule] = useState<DeduplicationRule | null>(null);
  const [loading, setLoading] = useState(true);

  const canWrite = hasPermission('data_management', 'write');

  const loadRule = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getDeduplicationRule(id);
      if (res.data?.success && res.data.data) {
        setRule(res.data.data);
      }
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadRule();
  }, [loadRule]);

  const handleRun = async () => {
    try {
      await runDeduplicationJob(id!);
      message.success(t('data_management.dedup.job.run_success'));
      navigate('/data-management/dedup-jobs');
    } catch {
      message.error(t('app.error'));
    }
  };

  if (loading || !rule) return null;

  const matchFieldColumns = [
    { title: t('data_management.dedup.rule.field.field_name', 'Field'), dataIndex: 'field_name', key: 'field_name' },
    {
      title: t('data_management.dedup.rule.field.match_type', 'Match Type'),
      dataIndex: 'match_type',
      key: 'match_type',
      render: (v: string) => t(`data_management.dedup.rule.match_types.${v}`, v),
    },
    { title: t('data_management.dedup.rule.field.weight', 'Weight'), dataIndex: 'weight', key: 'weight', width: 80 },
    {
      title: t('data_management.dedup.rule.field.case_sensitive', 'Case Sensitive'),
      dataIndex: 'case_sensitive',
      key: 'case_sensitive',
      width: 120,
      render: (v: boolean) => <Tag color={v ? 'blue' : 'default'}>{v ? t('app.yes') : t('app.no')}</Tag>,
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/data-management/dedup-rules')}>
          {t('app.back')}
        </Button>
        {canWrite && rule.is_active && (
          <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleRun}>
            {t('data_management.dedup.rule.run')}
          </Button>
        )}
      </Space>

      <Title level={4}>{rule.name}</Title>

      <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
        <Descriptions.Item label={t('data_management.dedup.rule.field.name')}>{rule.name}</Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.rule.field.is_active')}>
          <Tag color={rule.is_active ? 'green' : 'red'}>
            {rule.is_active ? t('app.yes') : t('app.no')}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.rule.field.description')} span={2}>
          {rule.description || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.rule.field.target_module')}>
          {t(`data_management.import.modules.${rule.target_module}`, rule.target_module)}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.rule.field.match_strategy')}>
          {t(`data_management.dedup.rule.match_strategies.${rule.match_strategy}`, rule.match_strategy)}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.rule.field.merge_strategy')}>
          {t(`data_management.dedup.rule.merge_strategies.${rule.merge_strategy}`, rule.merge_strategy)}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.rule.field.similarity_threshold')}>
          {(rule.similarity_threshold * 100).toFixed(0)}%
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.rule.field.auto_merge')}>
          <Tag color={rule.auto_merge ? 'orange' : 'default'}>
            {rule.auto_merge ? t('app.yes') : t('app.no')}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.rule.field.owner')}>{rule.owner_id}</Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.rule.field.total_duplicates_found')}>
          {rule.total_duplicates_found > 0 ? rule.total_duplicates_found.toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.rule.field.total_merged')}>
          {rule.total_merged > 0 ? rule.total_merged.toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.rule.field.last_run_at')}>
          {rule.last_run_at ? new Date(rule.last_run_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.created_at')}>
          {new Date(rule.created_at).toLocaleString()}
        </Descriptions.Item>
      </Descriptions>

      <Title level={5}>{t('data_management.dedup.rule.field.match_fields')} ({rule.match_fields.length})</Title>
      <Table
        dataSource={rule.match_fields}
        columns={matchFieldColumns}
        rowKey={(r: DeduplicationMatchField) => r.field_name}
        pagination={false}
        size="small"
      />
    </div>
  );
}
